import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  projectReferencesService,
  type ProjectReferencesListResult,
} from '@/shared/api/services/project-references.service';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { createLogger } from '@/shared/lib/logger';
import type { ProjectReferenceSection } from '@/types/project-reference';
import {
  exhaustedIds,
  nextPollDelayMs,
  planClassificationWatch,
  useClassificationWatchStore,
  type StalledReference,
} from '@/features/project-reference/lib/classification-watch';

const logger = createLogger('ReferenceClassification');

/**
 * Fallback poll for reference classification on one references list, in case
 * the socket verdict is lost.
 *
 * One loop per list, restarted only when the set of classifying references
 * changes, never on a plain refetch. Every reference waits at most the
 * server's allowance, counted from when the poll started waiting for it, so a
 * failed round (offline, 5xx, 403) spends it too and still waits the interval.
 * A reference still classifying after that stops being polled and shows the
 * last API refusal, or the timeout line, through the tile's failed state. It
 * is polled again only once the server reports a new version of the row; a
 * new classifying reference starts its own wait.
 *
 * Release of given-up rows runs for every list, polled or not, so a verdict
 * seen by any screen brings the server back in charge everywhere.
 */
export function useClassificationWatch(
  projectId: string,
  section: ProjectReferenceSection | undefined,
  options: { enabled: boolean; polling: boolean }
): void {
  const queryClient = useQueryClient();
  const t = useTranslations('angle.upload.modal');
  // Read, never re-subscribed on: a locale switch must not restart the wait.
  const timeoutMessageRef = useRef(t('validationTimeout'));
  timeoutMessageRef.current = t('validationTimeout');

  // Raw server rows of the list (the shared observer's `select` overlays
  // given-up rows as failed, which would read as settled here). Never fetches.
  const { data: raw } = useQuery({
    queryKey: cacheKeys.references.list(projectId, section),
    queryFn: () =>
      projectReferencesService.listProjectReferences(projectId, section),
    enabled: false,
  });

  const stalled = useClassificationWatchStore((state) => state.stalled);
  const waitingSince = useClassificationWatchStore(
    (state) => state.waitingSince
  );
  const plan = useMemo(
    () => planClassificationWatch(raw?.references ?? [], stalled, waitingSince),
    [raw, stalled, waitingSince]
  );
  const watchKey = plan.watchIds.join(',');

  useEffect(() => {
    if (plan.released.length > 0) {
      useClassificationWatchStore.getState().release(plan.released);
    }
  }, [plan]);

  const polling = options.enabled && options.polling && !!projectId;

  useEffect(() => {
    if (!polling || !watchKey) return;

    const ids = watchKey.split(',');
    const queryKey = cacheKeys.references.list(projectId, section);
    const store = useClassificationWatchStore.getState;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms);
      });

    const giveUp = (exhausted: string[], message: string) => {
      const rows =
        queryClient.getQueryData<ProjectReferencesListResult>(queryKey)
          ?.references ?? [];
      const entries: Record<string, StalledReference> = {};
      for (const row of rows) {
        if (exhausted.includes(row.id)) {
          entries[row.id] = { updatedAt: row.attributes.updated_at, message };
        }
      }
      logger.warn(
        ` ${exhausted.length} reference(s) of ${projectId} still classifying after the wait budget`
      );
      // Changes the watched set: this effect restarts for what is left.
      store().stall(entries);
    };

    const run = async () => {
      let lastRejection: string | undefined;
      store().startWaiting(ids, Date.now());

      for (;;) {
        const now = Date.now();
        const exhausted = exhaustedIds(ids, store().waitingSince, now);
        if (exhausted.length > 0) {
          giveUp(exhausted, lastRejection || timeoutMessageRef.current);
          return;
        }

        await sleep(nextPollDelayMs(ids, store().waitingSince, now));
        if (cancelled) return;

        try {
          await queryClient.refetchQueries(
            { queryKey, exact: true },
            { throwOnError: true }
          );
          lastRejection = undefined;
        } catch (error) {
          if (cancelled) return;
          // A failed round spends the wait too; it only keeps the refusal.
          logger.warn('Classification poll failed:', error);
          lastRejection =
            error instanceof ApiClientError ? error.message : undefined;
        }
        if (cancelled) return;
      }
    };

    void run();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [polling, watchKey, projectId, section, queryClient]);
}
